/**
 * DOM helpers used by the components as Svelte actions (`use:focusTrap`, `use:menuKeys`,
 * `use:menuPos`, `use:tooltip`, `use:stopArrowKeys`) plus the pure `placeMenu()` geometry.
 * Behaviour reproduces the legacy `an`, `qr`, `Oo`/`Sr`, `ae` and `ma` helpers.
 */

export interface ActionReturn<P = undefined> {
  update?: (param: P) => void;
  destroy?: () => void;
}

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Visible focusable descendants (fixed-position elements count as visible). */
export function focusables(root: ParentNode): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (el) => el.offsetParent !== null || getComputedStyle(el).position === 'fixed',
  );
}

/**
 * Trap Tab inside `node`, focus `initial` (selector) or the first focusable next frame, and
 * restore the previously focused element on destroy. Tab is left alone while a `<dialog open>`
 * exists, because the native dialog owns focus then.
 */
export function focusTrap(node: HTMLElement, opts: { initial?: string } = {}): ActionReturn {
  const previous = document.activeElement as HTMLElement | null;
  const raf = requestAnimationFrame(() => {
    const target = (opts.initial && node.querySelector<HTMLElement>(opts.initial)) || focusables(node)[0] || node;
    if (target === node && !node.hasAttribute('tabindex')) node.tabIndex = -1;
    target.focus({ preventScroll: true });
  });
  function onKey(ev: KeyboardEvent) {
    if (ev.key !== 'Tab' || document.querySelector('dialog[open]')) return;
    const list = focusables(node);
    if (!list.length) {
      ev.preventDefault();
      return;
    }
    const first = list[0]!;
    const last = list[list.length - 1]!;
    const active = document.activeElement;
    if (!node.contains(active)) {
      ev.preventDefault();
      first.focus();
      return;
    }
    if (ev.shiftKey && active === first) {
      ev.preventDefault();
      last.focus();
    } else if (!ev.shiftKey && active === last) {
      ev.preventDefault();
      first.focus();
    }
  }
  node.addEventListener('keydown', onKey);
  document.addEventListener('keydown', onKey, true);
  return {
    destroy() {
      cancelAnimationFrame(raf);
      node.removeEventListener('keydown', onKey);
      document.removeEventListener('keydown', onKey, true);
      if (previous && document.contains(previous)) previous.focus({ preventScroll: true });
    },
  };
}

const MENU_ITEMS = '[role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]';

/** ArrowUp/Down/Home/End cycle through enabled menu items inside `node`. */
export function menuKeys(node: HTMLElement): ActionReturn {
  const items = () => [...node.querySelectorAll<HTMLElement>(MENU_ITEMS)].filter((el) => !el.hasAttribute('disabled'));
  const focusAt = (i: number) => {
    const list = items();
    if (list.length) list[((i % list.length) + list.length) % list.length]!.focus();
  };
  function onKey(ev: KeyboardEvent) {
    const list = items();
    if (!list.length) return;
    const idx = list.indexOf(document.activeElement as HTMLElement);
    switch (ev.key) {
      case 'ArrowDown':
        ev.preventDefault();
        focusAt(idx + 1);
        break;
      case 'ArrowUp':
        ev.preventDefault();
        focusAt(idx - 1);
        break;
      case 'Home':
        ev.preventDefault();
        focusAt(0);
        break;
      case 'End':
        ev.preventDefault();
        focusAt(list.length - 1);
        break;
      default:
        return;
    }
  }
  node.addEventListener('keydown', onKey);
  return { destroy: () => node.removeEventListener('keydown', onKey) };
}

/** Stop Arrow/Home/End keys from bubbling out (the reader menu sits inside the list handler). */
export function stopArrowKeys(node: HTMLElement): ActionReturn {
  const keys = new Set(['ArrowUp', 'ArrowDown', 'Home', 'End']);
  const onKey = (ev: KeyboardEvent) => {
    if (keys.has(ev.key)) ev.stopPropagation();
  };
  node.addEventListener('keydown', onKey);
  return { destroy: () => node.removeEventListener('keydown', onKey) };
}

export interface MenuAnchor {
  /** Horizontal edge the menu hangs from (its right edge in RTL, left edge in LTR). */
  x: number;
  /** Top of the menu when it opens below the anchor. */
  y: number;
  /** Bottom of the menu when it must flip above the anchor. */
  above: number;
}

export const MENU_MARGIN = 8;
export const MENU_GAP = 6;
export const MENU_WIDTH = 220;

/**
 * Menu geometry (legacy `Sr`): a `width`-px menu anchored at `anchor`, RTL-aware (the x edge is
 * the menu's right edge in RTL), flipped above when it would overflow the viewport bottom and
 * there is room above, clamped to 8 px margins.
 */
export function placeMenu(anchor: MenuAnchor, width = MENU_WIDTH, height = 0): { top: number; left: number } {
  const rtl = document.documentElement.dir === 'rtl';
  const rawLeft = rtl ? anchor.x - width : anchor.x;
  const left = Math.min(Math.max(MENU_MARGIN, rawLeft), window.innerWidth - width - MENU_MARGIN);
  let top = anchor.y;
  if (top + height > window.innerHeight - MENU_MARGIN && anchor.above - height >= MENU_MARGIN) {
    top = anchor.above - height;
  }
  top = Math.min(Math.max(MENU_MARGIN, top), Math.max(MENU_MARGIN, window.innerHeight - height));
  return { top: Math.round(top), left: Math.round(left) };
}

/** Build a `MenuAnchor` from the button that opened the menu. */
export function anchorFromElement(el: Element): MenuAnchor {
  const r = el.getBoundingClientRect();
  return { x: r.right, y: r.bottom + MENU_GAP, above: r.top - MENU_GAP };
}

export interface MenuPosOptions {
  anchor: MenuAnchor | null;
  width?: number;
  set: (pos: { top: number; left: number }) => void;
}

/** Action: once mounted, measure the menu and hand a clamped position to `set`. */
export function menuPos(node: HTMLElement, opts: MenuPosOptions): ActionReturn {
  const raf = requestAnimationFrame(() => {
    if (!opts.anchor) return;
    const h = Math.max(node.getBoundingClientRect().height, node.scrollHeight);
    opts.set(placeMenu(opts.anchor, opts.width ?? MENU_WIDTH, h));
  });
  return { destroy: () => cancelAnimationFrame(raf) };
}

// ---- tooltip -------------------------------------------------------------------------------

const TIP_DELAY = 400;
const TIP_OFFSET = 8;
const TIP_MARGIN = 8;
let tipEl: HTMLDivElement | null = null;
let tipTimer = 0;
let tipOwner: HTMLElement | null = null;

function tipNode(): HTMLDivElement {
  if (!tipEl) {
    tipEl = document.createElement('div');
    tipEl.className = 'tik-tip';
    tipEl.setAttribute('role', 'tooltip');
    tipEl.setAttribute('aria-hidden', 'true');
    document.body.appendChild(tipEl);
  }
  return tipEl;
}

/** `"Label (K)"` renders as `<span>Label</span><kbd>K</kbd>`. */
export function renderTooltipText(el: HTMLElement, text: string): void {
  const m = text.match(/^(.*?)\s*\(([^()]{1,12})\)$/);
  el.textContent = '';
  const span = document.createElement('span');
  span.textContent = m ? m[1]! : text;
  el.appendChild(span);
  if (m) {
    const kbd = document.createElement('kbd');
    kbd.textContent = m[2]!;
    el.appendChild(kbd);
  }
}

function positionTip(tip: HTMLElement, owner: HTMLElement) {
  tip.style.visibility = 'hidden';
  tip.style.top = '0px';
  tip.style.left = '0px';
  tip.classList.add('on');
  const a = owner.getBoundingClientRect();
  const r = tip.getBoundingClientRect();
  let top = a.bottom + TIP_OFFSET;
  if (top + r.height > innerHeight - TIP_MARGIN) top = a.top - r.height - TIP_OFFSET;
  top = Math.max(TIP_MARGIN, Math.min(top, innerHeight - r.height - TIP_MARGIN));
  let left = a.left + a.width / 2 - r.width / 2;
  left = Math.max(TIP_MARGIN, Math.min(left, innerWidth - r.width - TIP_MARGIN));
  tip.style.top = Math.round(top) + 'px';
  tip.style.left = Math.round(left) + 'px';
  tip.style.visibility = '';
}

function hideTip() {
  clearTimeout(tipTimer);
  tipTimer = 0;
  tipOwner = null;
  tipEl?.classList.remove('on');
}

function hideTipIfShown() {
  if (tipEl?.classList.contains('on')) {
    tipEl.classList.remove('on');
    tipOwner = null;
  }
}

function showTip(owner: HTMLElement, text: string) {
  const tip = tipNode();
  tipOwner = owner;
  renderTooltipText(tip, text);
  positionTip(tip, owner);
}

/**
 * Tooltip action: text = argument or `aria-label`; shown after 400 ms of mouse hover (immediately
 * if one is already showing) or on `:focus-visible`; hidden on pointerdown/leave/blur, scroll,
 * resize and Escape. One global `div.tik-tip` is shared.
 */
export function tooltip(node: HTMLElement, text?: string): ActionReturn<string | undefined> {
  let read = () => text ?? node.getAttribute('aria-label') ?? '';
  const schedule = (delay: number) => {
    const s = read();
    if (!s) return;
    clearTimeout(tipTimer);
    tipTimer = window.setTimeout(() => showTip(node, s), delay);
  };
  const onEnter = (ev: PointerEvent) => {
    if (ev.pointerType === 'mouse') schedule(tipEl?.classList.contains('on') ? 0 : TIP_DELAY);
  };
  const onMove = (ev: PointerEvent) => {
    if (ev.pointerType !== 'mouse' || tipTimer || (tipOwner === node && tipEl?.classList.contains('on'))) return;
    schedule(TIP_DELAY);
  };
  const onLeave = () => {
    if (tipOwner === node || !tipOwner) hideTip();
  };
  const onFocus = () => {
    const s = read();
    if (s && node.matches(':focus-visible')) showTip(node, s);
  };
  node.addEventListener('pointerenter', onEnter);
  node.addEventListener('pointermove', onMove);
  node.addEventListener('pointerleave', onLeave);
  node.addEventListener('pointerdown', hideTip);
  node.addEventListener('focus', onFocus);
  node.addEventListener('blur', onLeave);
  return {
    update(next) {
      text = next;
      read = () => next ?? node.getAttribute('aria-label') ?? '';
    },
    destroy() {
      if (tipOwner === node) hideTip();
      node.removeEventListener('pointerenter', onEnter);
      node.removeEventListener('pointermove', onMove);
      node.removeEventListener('pointerleave', onLeave);
      node.removeEventListener('pointerdown', hideTip);
      node.removeEventListener('focus', onFocus);
      node.removeEventListener('blur', onLeave);
    },
  };
}

if (typeof window !== 'undefined') {
  window.addEventListener('scroll', hideTipIfShown, true);
  window.addEventListener('resize', hideTipIfShown);
  window.addEventListener('keydown', (ev: KeyboardEvent) => {
    if (ev.key === 'Escape') hideTip();
  });
}

/** Whether the app is in the desktop layout (split reading + docked compose). */
export function isDesktop(): boolean {
  return typeof matchMedia !== 'undefined' && matchMedia('(min-width: 1100px)').matches;
}

/** Whether the pointer is fine (hover row actions, context menus). */
export function isFinePointer(): boolean {
  return typeof matchMedia !== 'undefined' && matchMedia('(hover: hover) and (pointer: fine)').matches;
}

/** Whether the viewport is the mobile layout. */
export function isMobile(): boolean {
  return typeof matchMedia !== 'undefined' && matchMedia('(max-width: 900px)').matches;
}
